/**
 * BR-35 — when the lease Overview shows the compact "Tenant's checklist" banner.
 *
 * The ONLY place this rule lives; no component re-derives it.
 */

import type { WalkthroughPhase } from './condition.api';

/** A submitted tenant checklist stays on the Overview for this many days (BR-35 b). */
export const BANNER_SUBMITTED_DAYS = 14;
/** A not-started move-in checklist stays on the Overview for this many days after lease start (BR-35 c). */
export const BANNER_NOT_STARTED_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface BannerTenantSide {
  state: 'NOT_STARTED' | 'IN_PROGRESS' | 'SUBMITTED';
  submittedAt?: string | null;
}

export interface BannerLease {
  state: string;
  startDate: string;
}

export interface BannerInput {
  phase: WalkthroughPhase;
  tenant: BannerTenantSide;
  lease: BannerLease;
  now: Date;
}

/** The phase currently open for a lease state (move-in while scheduled/active, move-out while ending/ended). */
export function phaseForLeaseState(state: string): WalkthroughPhase | null {
  if (state === 'scheduled' || state === 'active') return 'MOVE_IN';
  if (state === 'ending' || state === 'ended') return 'MOVE_OUT';
  return null;
}

export function shouldShowChecklistBanner({ phase, tenant, lease, now }: BannerInput): boolean {
  if (lease.state === 'archived') return false;

  if (tenant.state === 'IN_PROGRESS') return true;

  if (tenant.state === 'SUBMITTED') {
    if (!tenant.submittedAt) return false;
    const submitted = new Date(tenant.submittedAt).getTime();
    if (Number.isNaN(submitted)) return false;
    return now.getTime() - submitted <= BANNER_SUBMITTED_DAYS * MS_PER_DAY;
  }

  // NOT_STARTED
  if (phase === 'MOVE_OUT') return lease.state === 'ending' || lease.state === 'ended';
  // MOVE_IN is only "in its window" while the lease is scheduled/active (BR-35).
  if (lease.state !== 'scheduled' && lease.state !== 'active') return false;
  const start = new Date(lease.startDate).getTime();
  if (Number.isNaN(start)) return false;
  return now.getTime() - start <= BANNER_NOT_STARTED_DAYS * MS_PER_DAY;
}
